import { memo } from 'react';
import { CgCalendarDates } from 'react-icons/cg';
import { PatternFormat } from 'react-number-format';
import { Icon, Input, Popover, Portal } from '@chakra-ui/react';

import { DATE_FORMATS } from '../../../constants';
import { InputGroup } from '../../../lib/chakraUi/components';
import { FormFieldControlledComponent } from '../../../types/fields';
import { useDatePicker } from './hooks/useDatePicker';
import { DatePickerInputProps } from './types/DatePickerInputProps';
import { DatePickerProps } from './types/DatePickerProps';
import { DatePickerPopover } from './DatePickerPopover';

const DatePickerComponent = ({
  _ref,
  availableDaysRange,
  disabled,
  invalid,
  name,
  readOnly,
  onDateChange,
  onChange,
  value,
  ...props
}: DatePickerInputProps) => {
  const {
    handleOnBlur,
    handleOnClick,
    handleOnFocus,
    handleTypeDate,
    inputContainerRef,
    inputTypeValue,
    mergedAvailableDateRange,
    selectedDate,
    setDate,
    setVisibleMonth,
    datePickerPopoverApi,
    visibleMonth,
    popoverContentRef,
    handleSetRef,
    handleInteractOutside,
  } = useDatePicker(_ref, onChange, onDateChange, value, availableDaysRange);
  const isDisabledOrReadOnly = disabled || readOnly;

  return (
    <Popover.Root
      autoFocus={false}
      closeOnEscape
      closeOnInteractOutside={false}
      onInteractOutside={handleInteractOutside}
      onOpenChange={datePickerPopoverApi.onOpenChange}
      open={datePickerPopoverApi.open}
      positioning={{ placement: 'bottom-end' }}
    >
      <Popover.Anchor asChild>
        <InputGroup
          ref={inputContainerRef}
          borderColor="gray.100"
          endElement={<Icon as={CgCalendarDates} />}
          endElementProps={{
            pointerEvents: 'none',
          }}
          onBlur={handleOnBlur}
          onClick={handleOnClick}
          onFocus={handleOnFocus}
          position="relative"
        >
          {/* FormField points the field label's `htmlFor` at `name`, so this
          id makes the input PatternFormat renders the labelled control */}
          <Input
            asChild
            disabled={isDisabledOrReadOnly}
            id={name}
            name={name}
            onChange={handleTypeDate}
            placeholder={DATE_FORMATS.standard.toUpperCase()}
            value={inputTypeValue || ''}
            {...props}
          >
            <PatternFormat format="##/##/####" getInputRef={handleSetRef} />
          </Input>
        </InputGroup>
      </Popover.Anchor>
      <Portal>
        <Popover.Positioner>
          <Popover.Content ref={popoverContentRef} bg="unset" border="unset">
            <DatePickerPopover
              endMonth={mergedAvailableDateRange.before}
              selectedDate={selectedDate}
              setDate={setDate}
              setVisibleMonth={setVisibleMonth}
              startMonth={mergedAvailableDateRange.after}
              visibleMonth={visibleMonth}
            />
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

export const DatePicker = memo(
  DatePickerComponent,
) as FormFieldControlledComponent<string, DatePickerProps>;

DatePicker.controlled = true;
DatePicker.displayName = 'DatePicker';
